import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Monitor, Smartphone, Tablet } from "lucide-react";
import { useLanguage } from "@/contexts/useLanguage";

interface Device {
  id: string;
  device_name: string;
  device_type: string;
  ip_address: string;
  last_seen: string;
  location_info?: {
    timezone?: string;
    platform?: string;
  };
}

interface ActiveSessionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onForceLogin: () => void;
  devices: Device[];
  isLoading?: boolean;
}

const getDeviceIcon = (deviceType: string) => {
  switch (deviceType.toLowerCase()) {
    case 'mobile':
      return <Smartphone className="h-4 w-4" />;
    case 'tablet':
      return <Tablet className="h-4 w-4" />;
    default:
      return <Monitor className="h-4 w-4" />;
  }
};

export const ActiveSessionDialog = ({
  isOpen,
  onClose,
  onForceLogin,
  devices,
  isLoading = false
}: ActiveSessionDialogProps) => {
  const { t } = useLanguage();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleForceLogin = async () => {
    setIsConfirming(true);
    try {
      await onForceLogin();
    } finally {
      setIsConfirming(false);
    }
  };

  const formatLastSeen = (lastSeen: string) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('auth.activeSession.justNow');
    if (diffMins < 60) return t('auth.activeSession.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('auth.activeSession.hoursAgo', { count: diffHours });
    return t('auth.activeSession.daysAgo', { count: diffDays });
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {t("auth.activeSession.title")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            {t("auth.activeSession.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <h4 className="text-sm font-medium">{t("auth.activeSession.activeDevices")}</h4>
          {devices.map((device) => (
            <div
              key={device.id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50"
            >
              {getDeviceIcon(device.device_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{device.device_name}</p>
                <p className="text-xs text-muted-foreground">
                  {device.ip_address} • {formatLastSeen(device.last_seen)}
                </p>
                {device.location_info?.platform && (
                  <p className="text-xs text-muted-foreground">
                    {device.location_info.platform}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onClose} className="w-full sm:w-auto">
            {t("common.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleForceLogin}
            disabled={isConfirming || isLoading}
            className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isConfirming ? t("auth.activeSession.loggingOut") : t("auth.activeSession.logOutOtherDevices")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};